import React, { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useFormContext } from "react-hook-form";
import Select from '../../../components/select';
import CustomerUpdate from "../../../components/forms/customer-update";
import CustomerCreate from "../../../components/forms/customer-create";
import { getCustomers, getCustomerDetail, addSubscription, clearSelectedCustomer } from "../../../slices/customers";
import { updateSubscription } from "../../../slices/subscriptions";
import { createAddress } from "../../../slices/address";
import { getPlans } from "../../../slices/plans";
import { useNavigate } from 'react-router-dom';
import PaymentForm from "../../../components/forms/payment-form";
import SubscriptionForm from "../../../components/forms/subscription-form";
import DeliveryAddressForm from "../../../components/forms/delivery-address-form";
import { formatMoney } from "../../../utils";
import { getIntervalTranslation } from "../../../const";
import useFilter from "../../../hooks/filter";

const FS = {
    'initialState': 0,
    'chooseCustomer': 1,
    'choosePlan': 2,
    'choosePayment': 3,
    'completeState': 4
}

const SubscriptionNew = () => {
    const dispatch = useDispatch()
    const methods = useForm();
    const navigate = useNavigate()
    const fetchCustomersPromise = useRef()
    const customers = useSelector((state) => state.customers.results)
    const selectedCustomer = useSelector((state) => state.customers.selectedCustomer)
    const [formState, setFormState] = useState(FS['initialState']);
    const [addDeliveryAddress, setAddDeliveryAddress] = useState(false);
    const [searchString, setSearchString] = useState("");
    const [saveDisabled, setSaveDisabled] = useState(true);
    const [isDirty, setIsDirty] = useState(true)
    const watchFields = methods.watch(["plan", "payment"])

    const {
        activeFilters,
        addFilter,
        removeFilter,
        changeFilterValue,
        replaceFilter,
        updateSearchFilter,
    } = useFilter()

    useEffect(() => {
        dispatch(getCustomers());
        dispatch(getPlans());
    }, [])

    useEffect(() => {
        setSaveDisabled(!(watchFields[0] && watchFields[1]))
    }, [watchFields])

    useEffect(() => {
        if (isDirty) {
            const params = [ ...activeFilters ]

            fetchCustomersPromise.current?.abort()
            let ignore = false
            if (!ignore) {
                fetchCustomersPromise.current = dispatch(getCustomers(params))
                setIsDirty(false)
            }
            return () => {
                ignore = true;
            };
        }
    }, [isDirty])

    const fetchSelectedCustomer = (id) => {
        dispatch(getCustomerDetail({ customerId: id }))
            .then(setFormState(FS['choosePlan']))
    }

    const onDeliveryAddressChange = (data) => {
        methods.setValue('deliveryAddress', data)
        // dispatch(updateAddress({ id: deliveryAddress.id, args: newAddress }))
    }

    const subscriptionFormCallback = (data) => {
        methods.setValue('plan', data)
        setFormState(FS['choosePayment'])
    }

    const saveSubscription = () => {
        // TODO Also add delivery address
        Promise.resolve(dispatch(addSubscription({customerId: selectedCustomer.id, args: methods.getValues()})))
        .then(data => {
            if (data.meta.requestStatus === "rejected") {
                // Do something here
            } else {
                let subscriptionId = data.payload.id;
                let addr = methods.getValues('deliveryAddress')
                if (addr){
                    dispatch(createAddress(addr)).then((promise) => { 
                        dispatch(updateSubscription({subscriptionId: subscriptionId, args: { delivery_address: promise.payload.id } }))
                            .then(() => dispatch(getSubscriptionDetail({ subscriptionId: subscriptionId })))
                        })
                    }
                navigate(`/subscriptions/${subscriptionId}`)
            }
        })
    }

    const goToNewCustomer = () => {
        dispatch(clearSelectedCustomer())
        setFormState(FS['chooseCustomer'])
    }

    const setPaymentInfo = (paymentMethod, paymentData) => {
        methods.setValue('payment', { 'type': paymentMethod, 'data': paymentData })
    }

    return (
        <FormProvider {...methods}>
            <div className="center subscription-new">
                <div className="container-fluid pb-5">
                    <div className="row clamped mx-auto">
                        <div className="col px-0 settings-header">
                            <h1 className="title">{gettext("New Subscription")}</h1>
                            {formState >= FS['chooseCustomer'] &&
                                 <button className="btn btn-secondary btn-sm" onClick={() => { setFormState(FS['initialState']) }}>{gettext('Select another customer')}</button>
                            }
                        </div>
                    </div>
                    <div className="row contentarea clamped mx-auto mb-5">
                        <div className="container-fluid detail">
                            {formState === FS['initialState'] &&
                                <div className="row">
                                    <div className="col-md-6 col-12 mb-4">
                                        <Select
                                            options={customers}
                                            // menuIsOpen
                                            getOptionValue={(customer) => customer.id}
                                            getOptionLabel={(customer) => customer.name}
                                            onChange={(customer) => {
                                                setFormState(FS['chooseCustomer'])
                                                fetchSelectedCustomer(parseInt(customer.id));
                                            }}
                                            placeholder={gettext('Select existing customer')}
                                            onInputChange={(value, action) => {
                                                // Only do if user actually changes input
                                                if (action.action === 'input-change'){
                                                    updateSearchFilter(value)
                                                    setSearchString(value)
                                                    setIsDirty(true)
                                                }
                                            }}
                                            // Prevent input clearing on clickoutside etc
                                            inputValue={searchString}
                                        />
                                    </div>
                                    <div className="col-md-6 col-12 mb-4">
                                        <button className="form-control btn btn-primary" onClick={() => goToNewCustomer() }>{gettext('New customer')}</button>
                                    </div>
                                </div>
                            }
                            {formState >= FS['chooseCustomer'] &&
                                <>
                                    {selectedCustomer ?
                                        <>
                                            <CustomerUpdate customer={selectedCustomer} />
                                            {addDeliveryAddress ?
                                                <>
                                                    <h2>{gettext('Delivery address')}</h2>
                                                    <DeliveryAddressForm onChange={onDeliveryAddressChange} />
                                                </>
                                                :
                                                <button className="btn btn-secondary btn-sm" onClick={() => setAddDeliveryAddress(true)}>{gettext('Add delivery address (if different from customer address)')}</button>
                                            }
                                        </>
                                        :
                                        <CustomerCreate onSuccess={fetchSelectedCustomer} />
                                    }
                                </>
                            }
                        </div>
                    </div>
                    {formState >= FS['choosePlan'] &&
                        <div className="row contentarea clamped mx-auto mb-5">
                            <div className="container-fluid detail">
                                <SubscriptionForm callback={subscriptionFormCallback} />
                            </div>
                        </div>
                    }
                    {formState >= FS['choosePayment'] &&
                        <>
                            <div className="row contentarea clamped mx-auto mb-5">
                                <div className="container-fluid detail">
                                    <h2>{gettext("Payment method")}</h2>
                                    <PaymentForm callback={setPaymentInfo} />
                                </div>
                            </div>
                            <div className="clamped text-end mx-auto">
                                <button className={`form-control btn btn-primary ${saveDisabled ? 'disabled' : ''}`} onClick={saveSubscription}>{gettext('Save')}</button>
                            </div>
                        </>
                    }
                </div>
            </div>
        </FormProvider>
    )

}

export default SubscriptionNew